<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'

/*
*  форма создания и редактирования заявок (тикетов)
*
* Основные функции:
* - Создание новых заявок с выбором службы, категории и приоритета
* - Редактирование существующих заявок с управлением координаторами и исполнителями
* - Прикрепление файлов к заявкам
* - Назначение ответственности и отслеживание выполнения
* - Завершение заявок с проверкой выполнения всех задач
*
* Особенности:
* - Адаптивный интерфейс для разных размеров экрана
* - Вкладки для разных ролей (форма, координатор, задачи)
* - Валидация обязательных полей перед отправкой
* - Интеграция с системой файлов и деревом категорий
*/

// Импорты Vue и Vue Router
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

// Импорты Vuetify
import { useDisplay } from 'vuetify/framework'

// Импорты утилит
import { getApiError } from '@/composables/getApiError'
import { formatDateTime, getFullName } from '@/utils/formatters'
import { isHttpNotFound } from '@/utils/httpErrors'
import { openChat } from '@/utils/openChat'
import type { ITicketService } from '../types/ITicketService'

// Импорты хранилищ
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useTicketsStore } from '@/modules/tickets/stores/ticketsStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'

// Импорты типов
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
// Импорты компонентов
import TaskCard from '@/modules/tasks/components/TaskCard.vue'
import TaskUnassignedCard from '@/modules/tasks/components/TaskUnassignedCard.vue'
import BridgedEmbeddedChat from '@/components/chat/BridgedEmbeddedChat.vue'
import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import EntityPaneEar from '@/components/createdUi/EntityPaneEar.vue'
import DownloadEntityDocument from '@/components/createdUi/DownloadEntityDocument.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import EntityAccessDeniedOverlay from '@/components/createdUi/EntityAccessDeniedOverlay.vue'
import OnboardingDemoChat from '@/modules/onboarding/components/OnboardingDemoChat.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { TICKETS_ANCHORS, TICKETS_CONTEXTS } from '@/modules/onboarding/scenarios/tickets/anchors'
import { TICKETS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/tickets/adapter'
import EntityFormSummary from '@/components/createdUi/EntityFormSummary.vue'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import { fetchTicketCategories, searchDepartments, searchHospitalTree, searchTicketServices } from '@/components/createdUi/treeSearch'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useEmbeddedChatSetup } from '@/composables/useChatModuleBridge'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import FileList from '@/modules/files/components/FileList.vue'
import CoordinatorReport from '@/views/AdverseEvents/CoordinatorReport.vue'

/**
 * Входящие параметры компонента TicketForm.
 *
 * @property {number} [ticketId] - Идентификатор заявки (тикета). Задается из `feedbackStore.globalTicketId` при открытии формы глобально поверх текущей страницы (например, по клику на уведомление с типом urlSource: 'Ticket'). При стандартном роутинге ID берется из `route.params.id`.
 * @property {boolean} [isGlobal=false] - Флаг глобального модального открытия формы (в обход роутера, например, из колокольчика уведомлений).
 * Если `true`:
 * 1. Идентификатор заявки берется исключительно из пропса `ticketId`.
 * 2. При закрытии формы не происходит перенаправление по роутеру на родительскую страницу (не вызывается `goToParentRoute()`), вместо этого сбрасывается `feedbackStore.globalTicketId` в null, что скрывает модальное окно.
 * 3. Локальные состояния видимости диалогов изолируются.
 */
const props = withDefaults(defineProps<{
  ticketId?: number
  isGlobal?: boolean
}>(), {
  isGlobal: false,
})

// Инициализация утилит отображения Vuetify
const { mdAndUp, smAndDown, xs } = useDisplay()

const entityId = computed(() => props.isGlobal ? props.ticketId : (route.params.id ? Number(route.params.id) : null))

// Инициализация хранилищ
const tickets = useTicketsStore()
const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const userStore = useUserStore()
const filesStore = useFilesStore()
const permissionsStore = usePermissionsStore()
const route = useRoute()

// Деструктуризация реактивных значений из хранилищ
const { currentTicket, editableTicket } = storeToRefs(tickets)

// Реактивные состояния управления UI
// Видимость диалога незавершенных задач
const showUncompletedTasksDialog = ref(false)

// Видимость выбора отделений
const showDepartmentSelect = ref(false)

// Видимость диалога несохраненных изменений
const showUnsavedChangesDialog = ref(false)


// Видимость модалки профиля сотрудника
const isEmployeeModalOpen = ref(false)

// ID сотрудника для просмотра профиля
const employeeId = ref<string>('')

// Видимость модалки выбора помещения
const showRoomModal = ref(false)

// Видимость дерева категорий
const showCategoryTree = ref(false)

// Видимость выбора службы
const showServiceSelect = ref(false)

// Видимость основной формы
const visible = ref(true)

// Активная вкладка формы
const formTabValue = ref<number>(0)
const isAccessDenied = ref(false)

// Референсы компонентов
// Референс на модалку выбора координатора
const coordinatorModalRef = ref()

// Выбранный ID координатора
const selectedCoordinatorId = ref<string>('')

// Референс на компонент прикрепления файлов
const fileAttachRef = ref()

// Состояния для работы с файлами
// Локальный список новых файлов для заявки
const ticketFiles = ref<File[]>([])

// Список ID файлов помеченных на удаление
const ticketRemovedFileIds = ref<number[]>([])

// Существующие файлы прикрепленные к заявке
const existingTicketFiles = computed<IExistingFile[]>(() => (tickets.currentTicket as any)?.files ?? [])

// Вычисляемые свойства адаптивного дизайна
// Ширина для широких полей (75% на desktop, 100% на mobile)
const responsiveWidth = computed(() => {
  return mdAndUp.value ? 'w-75' : 'w-100'
})

// Вычисляемые свойства данных формы
// Текущий объект заявки
const getCurrentTicket = computed(() => currentTicket.value)

const isCreator = computed(() => currentTicket.value.created_by?.id === userStore.user?.id)

const creatorDepartmentTooltip = computed(() => {
  return currentTicket.value.created_by?.department_name
    || currentTicket.value.department?.name
    || findTreeNodeLabel(apiStore.departmentsTree, currentTicket.value.department?.id)
    || 'Отделение не указано'
})

// Описание заявки (геттер/сеттер)
const description = computed({
  get: () => currentTicket.value?.description || '',
  set: val => {
    if (currentTicket.value) currentTicket.value.description = val
  },
})

// Контактный телефон (геттер/сеттер)
const contactNumber = computed({
  get: () => currentTicket.value?.contact_number || '',
  set: val => {
    if (currentTicket.value) currentTicket.value.contact_number = val
  },
})

// Флаг высокого приоритета (геттер/сеттер)
const isHighPriority = computed({
  get: () => currentTicket.value?.is_high_priority || false,
  set: val => {
    if (currentTicket.value) currentTicket.value.is_high_priority = val
  },
})

// Флаг режима редактирования (true если есть id в маршруте)
const isEdit = computed(() => Boolean(entityId.value))

/*
 * Демо-режим онбординга. Форма открыта на учебной заявке, если её id — из демо-данных онбординга
 * (переход из таблицы) или онбординг запущен из формы реальной заявки (demoOverrideId).
 * Id учебной заявки фиктивный: в демо-режиме форма ничего не запрашивает по нему и не открывает Matrix-чат.
 */
const onboardingStore = useOnboardingStore()
const demoOverrideId = ref<number | null>(null)
const demoItem = computed(() => onboardingStore.findDemoItem('ticket', demoOverrideId.value ?? entityId.value))
const isDemo = computed(() => Boolean(demoItem.value))
const isChatEnabled = computed(() => isEdit.value && !isDemo.value)

// Учебная переписка в правой панели вместо Matrix-чата
const isDemoChatVisible = ref(false)

function toggleDemoChat() {
  isDemoChatVisible.value = !isDemoChatVisible.value
  if (isDemoChatVisible.value)
    isTasksPaneVisible.value = false
}

function applyDemoTicket() {
  if (!demoItem.value)
    return

  tickets.currentTicket = JSON.parse(JSON.stringify(demoItem.value.detail))
  tickets.editableTicket = JSON.parse(JSON.stringify(demoItem.value.detail))
  tickets.ticketLogs = []
  isAccessDenied.value = false
}

/**
 * Единая точка настройки embedded-режима карточки заявки: раскладка VDialog,
 * групповой чат заявки, ресайзабельная ширина правой панели. Композбл общий
 * с формой НС — отличаются только тип сущности (`appLabel`/`modelName`),
 * источник `objectId` и ключ localStorage (чтобы ширины чатов не пересекались
 * между сущностями).
 */
const { formEmbeddedLayout, entityChat, chatPaneResizer, contentClass: dialogContentClass } = useEmbeddedChatSetup({
  isEditMode: isEdit,
  /**
   * Включаем логику entity-чата на всём протяжении редактирования:
   * ищем/создаём Matrix-комнату type=event по event_url (без DAOIT REST).
   */
  chatEnabled: isChatEnabled,
  chat: {
    appLabel: 'ticket',
    modelName: 'Ticket',
    objectId: computed(() => {
      const id = entityId.value
      return !isDemo.value && typeof id === 'number' && Number.isFinite(id) ? id : null
    }),
  },
  storageKey: 'ticket-chat-pane',
})

const isTasksPaneVisible = ref(false)

/*
 * «Ушки» задач и чата работают как вкладки правой панели (как в форме аудита):
 * клик по неактивному — переключает панель на него, по активному — скрывает панель.
 */
const isTasksPaneActive = computed(() => isTasksPaneVisible.value && hasTasksPane.value)
const isChatPaneActive = computed(() => !isTasksPaneActive.value && entityChat.isChatVisible.value && entityChat.isAvailable.value)

// Без ушек (мобилка) правой панели нет — задачи открываются под шапкой формы вместо её тела
const isMobileTasksView = computed(() => !formEmbeddedLayout.isEmbedded.value && isTasksPaneActive.value)

const toggleTasksPane = () => {
  if (isTasksPaneActive.value) {
    isTasksPaneVisible.value = false
    return
  }
  isTasksPaneVisible.value = true
  isDemoChatVisible.value = false
  if (entityChat.isChatVisible.value)
    entityChat.hideChat()
}

const toggleChatPane = () => {
  if (isChatPaneActive.value) {
    entityChat.hideChat()
    return
  }
  isTasksPaneVisible.value = false
  entityChat.showChat()
}

// globalFormTab = 2 — сигнал «открыть панель задач»; immediate, т.к. выставляется до монтирования формы
watch(() => feedbackStore.globalFormTab, tab => {
  if (tab === 2) {
    isTasksPaneVisible.value = true
    if (entityChat.isChatVisible.value) {
      entityChat.hideChat()
    }
    feedbackStore.globalFormTab = null
  }
}, { immediate: true })

const isCreateMode = computed(() => !isEdit.value)
const pinCreateChrome = computed(() => isCreateMode.value)

// Панель задач есть, если задача координатора уже создана или её можно создать назначением координатора
const hasTasksPane = computed(() => Boolean(currentTicket.value?.coordinator_entry) || (isEdit.value && canEditCoordinator.value))

const isRightPaneVisible = computed(() =>
  (entityChat.isChatVisible.value && entityChat.isAvailable.value)
  || (isTasksPaneVisible.value && hasTasksPane.value)
  || isDemoChatVisible.value,
)

const coordinatorTask = computed(() => {
  if (!currentTicket.value?.coordinator_entry) return null
  return {
    ...currentTicket.value.coordinator_entry,
    is_coordinator_entry: true,
    source_type: 'ticket' as const,
    url_props: {
      ticketId: entityId.value ? Number(entityId.value) : undefined,
    },
  } as any
})

const handleCoordinatorTaskUpdated = (updatedTask: any) => {
  if (currentTicket.value?.coordinator_entry) {
    currentTicket.value.coordinator_entry = {
      ...currentTicket.value.coordinator_entry,
      ...updatedTask,
    }
    if (tickets.editableTicket?.coordinator_entry) {
      tickets.editableTicket.coordinator_entry = {
        ...tickets.editableTicket.coordinator_entry,
        ...updatedTask,
      }
    }
  }
}

const overlayContentClass = computed(() => {
  if (formEmbeddedLayout.contentClass.value) {
    return [
      formEmbeddedLayout.contentClass.value,
      { 'entity-form-embedded--no-chat': !isRightPaneVisible.value },
    ]
  }
  if (pinCreateChrome.value)
    return 'entity-form-create-dialog'
  return undefined
})

// Текст главное кнопки в зависимости от режима
const actionButtonText = computed(() => isEdit.value ? 'Сохранить' : 'Создать')

// Вычисляемое свойство для проверки доступности редактирования конкретного поля
const canEditField = (fieldName?: string): boolean => {
  // Нет ID = создание = можно редактировать всё
  if (!entityId.value) return true

  // Если не передали fieldName = проверяем только режим редактирования
  if (!fieldName) return true // или false в зависимости от логики

  // Проверяем конкретное поле
  const editableFields = currentTicket.value.editable_fields || []
  return editableFields.includes(fieldName)
}

const cardTitle = computed(() => {
  if (isEdit.value && !currentTicket.value.number) return 'Загрузка...'
  if (isEdit.value && currentTicket.value.number) return `Заявка №${currentTicket.value.number}`
  else return 'Создание новой заявки'
})

// Вычисляемые свойства проверки прав доступа
// true если пользователь координатор этой заявки
const isCoordinator = computed(() => !!currentTicket.value.coordinator_entry)


// true если заявка завершена (полный readonly)
const isCompleted = computed(() => currentTicket.value?.status === 'completed')

// true если можно взять заявку в работу
const canTake = computed(() => currentTicket.value.coordinator_entry?.actions?.includes('in-progress') && isCoordinator.value)

// true если можно добавить исполнителя
const canAddExecutor = computed(() =>
  currentTicket.value.coordinator_entry?.actions?.includes('transfer'),
)

// true если пользователь может управлять заявкой
const isManager = computed(() => !isCompleted.value && isCoordinator.value)

// true если можно завершить заявку
const canComplete = computed(() =>
  isCoordinator.value && currentTicket.value.coordinator_entry?.actions?.includes('complete'),
)

// Является ли пользователь членом группы исполнителей данной категории заявки
const isCategoryPerformer = computed(() => Boolean(currentTicket.value.is_performer))

// Разрешение на выбор произвольного координатора через модальное окно (право coordinator_edit)
const canPickCoordinator = computed(() => {
  return Boolean(permissionsStore.modules.ticket?.coordinator_edit)
})

// Разрешение на смену/назначение координатора (право координатора заявок, исполнитель группы или editable_fields с бекенда)
const canEditCoordinator = computed(() => {
  return Boolean(
    canPickCoordinator.value
    || isCategoryPerformer.value
    || canEditField('coordinator_entry'),
  )
})

/*
 * Выбор произвольного координатора: модульное право coordinator_edit или инспектор
 * (coordinator_entry в editable_fields). Исполнитель группы может только назначить себя (canAssignMyself).
 */
const canPickAnyCoordinator = computed(() => {
  return Boolean(canPickCoordinator.value || (canEditField('coordinator_entry') && !isCategoryPerformer.value))
})

// Возможность назначить себя координатором:
// доступно сотруднику из группы исполнителей (is_performer), если заявка существует, не завершена и координатор/исполнитель ещё не назначен
const canAssignMyself = computed(() => {
  return Boolean(
    isEdit.value
    && !isCompleted.value
    && isCategoryPerformer.value
    && Boolean(userStore.user?.id)
    && !currentTicket.value.coordinator_entry?.responsible_employee,
  )
})

// Вычисляемые свойства для работы с категориями и службами

/**
 * Вычисляемое свойство для работы с выбранной службой заявки
 *
 * Назначение:
 * - Двусторонняя привязка выбранной службы с данными заявки
 * - Автоматический выбор категории при определенных условиях
 *
 * Особенности:
 * - При изменении службы проверяет количество её категорий
 * - Если у службы ровно одна категория - выбирает её автоматически
 * - Если категорий несколько или нет - оставляет выбор пользователю
 *
 * Геттер:
 * - Возвращает ID выбранной службы из currentTicket или null
 *
 * Сеттер:
 * - Устанавливает выбранную службу в currentTicket
 * - Находит службу в общем списке services
 * - Проверяет количество категорий у этой службы
 * - Если категория одна - находит её ключ в categoryTree и устанавливает
 */
// Рекурсивный поиск всех категорий службы в дереве
function findCategoriesOfServices(nodes: ITreeNode[], serviceIds: string[]): ITreeNode[] {
  const result: ITreeNode[] = []
  function traverse(list: ITreeNode[]) {
    for (const node of list) {
      if (node.data?.serviceId && serviceIds.includes(node.data.serviceId)) {
        result.push(node)
      }
      if (node.children && node.children.length > 0) {
        traverse(node.children)
      }
    }
  }
  traverse(nodes)
  return result
}

const selectedService = computed({
  get: () => currentTicket.value?.service?.id ?? null,
  set: serviceId => {
    // 1 Устанавливаем выбранную службу в данные заявки
    if (currentTicket.value) {
      currentTicket.value.service = serviceId ? { id: serviceId } : null
    }
    // 2 Автоматический выбор категории при определенных условиях
    if (serviceId) {
      // Рекурсивно находим все категории этой службы и ее дочерних служб по дереву
      const serviceIds = getServiceIdsWithChildren(tickets.services, serviceId)
      const categoriesOfThisService = findCategoriesOfServices(tickets.categoryTree, serviceIds)
      // Проверяем: если у службы РОВНО ОДНА категория
      if (categoriesOfThisService.length === 1) {
        // Автоматически устанавливаем эту категорию как выбранную
        selectedCategory.value = categoriesOfThisService[0].key
      } else {
        // В случае когда изначально была выбрана служба с одной категорией а после этого пользователь выбрал службу с несколькими категориями очищаем текущие категории
        selectedCategory.value = null
        currentTicket.value.category = null
      }
    } else {
      // Если служба сброшена в null, также сбрасываем категорию
      selectedCategory.value = null
      if (currentTicket.value) {
        currentTicket.value.category = null
      }
    }
  },
})

const clearServiceAndCategory = () => {
  selectedCategory.value = null
  selectedService.value = null
  currentTicket.value.service = null
  currentTicket.value.category = null
}

/**
 * Обработчик выбора отделения
 */
function onDepartmentSelect(value: { id: string } | null) {
  currentTicket.value.department = value
}

/**
 * Обработчик выбора службы
 */
function onServiceSelect(value: { id: string } | null) {
  const serviceId = value ? value.id : null
  if (currentTicket.value && currentTicket.value.service?.id !== serviceId) {
    selectedService.value = serviceId
  }
}

/**
 * Вычисляемое свойство для работы с выбранной категорией заявки
 *
 * Назначение:
 * - Синхронизирует выбор категории между деревом категорий и данными заявки
 * - Преобразует числовой ID категории в ключ узла дерева и обратно
 * - Автоматически обновляет связанную службу при выборе категории
 *
 * Геттер:
 * - Преобразует числовой ID категории из currentTicket в ключ узла дерева
 * - Использует findKeyById для поиска соответствия в дереве категорий
 * - Возвращает null если категория не выбрана или не найдена
 *
 * Сеттер:
 * - При получении ключа категории находит соответствующий узел в дереве
 * - Устанавливает числовой ID категории в currentTicket
 * - Автоматически устанавливает службу из данных категории если она указана
 * - При получении null сбрасывает категорию к значению по умолчанию (id: 0)
 * - Закрывает модальное окно дерева категорий после установки значения
 *
 * Особенности:
 * - Обеспечивает двустороннюю привязку между деревом категорий и данными формы
 * - Поддерживает автоматическое определение службы по выбранной категории
 * - Сохраняет целостность данных между категорией и службой
 */
const selectedCategory = computed({
  get: () => {
    if (!currentTicket.value?.category?.id) return null
    return findKeyById(tickets.categoryTree, currentTicket.value.category.id)
  },
  set: (categoryKey: string | null) => {
    if (categoryKey && currentTicket.value) {
      // Находим узел категории и обновляем currentTicket
      const categoryNode = findCategoryNode(tickets.categoryTree, categoryKey)
      if (categoryNode && categoryNode.data?.selfId) {
        currentTicket.value.category = {
          id: String(categoryNode.data.selfId),
        }

        // Также обновляем службу если она есть в данных категории
        const serviceId = categoryNode.data.serviceId
        if (serviceId) {
          if (!currentTicket.value.service?.id || currentTicket.value.service.id !== serviceId) {
            currentTicket.value.service = { id: serviceId }
          }
        }
      }
    } else if (currentTicket.value) {
      currentTicket.value.category = null
    }

    showCategoryTree.value = false
  },
})

// Список ID выбранной службы и всех её дочерних служб
const selectedServiceIds = computed<string[]>(() => {
  const serviceId = selectedService.value
  if (!serviceId) return []
  return getServiceIdsWithChildren(tickets.services, serviceId)
})

// Рекурсивная фильтрация дерева категорий по списку разрешенных служб
function filterTreeByServices(nodes: ITreeNode[], serviceIds: string[]): ITreeNode[] {
  if (serviceIds.length === 0) return nodes
  return nodes
    .map(node => {
      const filteredChildren = node.children ? filterTreeByServices(node.children, serviceIds) : []
      const matches = node.data.serviceId && serviceIds.includes(node.data.serviceId)
      const hasValidChildren = filteredChildren.length > 0
      if (matches || hasValidChildren) {
        return {
          ...node,
          children: filteredChildren,
        }
      }
      return null
    })
    .filter((node): node is ITreeNode => node !== null)
}
// Дерево категорий (все или отфильтрованное по службе и её дочерним службам)
const categoryTree = computed(() => {
  return filterTreeByServices(tickets.categoryTree, selectedServiceIds.value)
})

async function searchCategoryTree(query: string): Promise<ITreeNode[]> {
  const rows = await fetchTicketCategories({ search: query, is_active: true })
  return filterTreeByServices(tickets.buildCategoryTree(rows), selectedServiceIds.value)
}

async function searchServiceTree(query: string): Promise<ITreeNode[]> {
  const rows = await searchTicketServices(query)
  return tickets.buildServicesTree(rows)
}



/**
 * Открытие деталей сотрудника
 *
 * Назначение:
 * - Показывает модальное окно с профилем сотрудника
 * - Используется для просмотра информации о создателе/координаторе
 *
 * Входные данные:
 * - id: string - идентификатор сотрудника
 */
const openEmployeeDetail = (id: string) => {
  employeeId.value = id
  isEmployeeModalOpen.value = true
}

/**
 * Поиск ключа узла в дереве категорий по числовому ID
 *
 * Назначение:
 * - Восстанавливает выбор в TreeSelect при редактировании заявки
 * - Преобразует числовой ID категории в строковый ключ узла
 *
 * Алгоритм:
 * - Рекурсивно обходит дерево категорий
 * - Сравнивает selfId узлов с искомым ID
 * - Возвращает ключ первого найденного узла
 *
 * Входные данные:
 * - nodes: ITreeNode[] - дерево категорий для поиска
 * - id: number - числовой идентификатор искомой категории
 *
 * Возвращает:
 * - string | null - ключ узла (например, `c-12-34`) или null если не найден
 */
const findKeyById = (nodes: ITreeNode[], id: string): string | null => {
  for (const n of nodes) {
    if (n.data?.selfId === id) return n.key
    if (n.children?.length) {
      const hit = findKeyById(n.children, id)
      if (hit) return hit
    }
  }
  return null
}

/**
 * Переключает видимость дерева категорий
 *
 * Назначение:
 * - Управляет отображением/скрытием модального окна с деревом категорий
 * - Используется при клике на иконку стрелки в поле выбора категории
 */
const toggleCategoryTree = () => {
  showCategoryTree.value = !showCategoryTree.value
}

/**
 * Обработчик выбора категории из дерева
 *
 * Назначение:
 * - Устанавливает выбранную категорию в данные заявки
 * - Автоматически определяет и устанавливает службу для выбранной категории
 * - Закрывает модальное окно дерева категорий после выбора
 *
 * Алгоритм:
 * 1. Находит узел категории по ключу
 * 2. Устанавливает службу из данных узла категории
 * 3. Устанавливает категорию в данные заявки
 * 4. Закрывает модальное окно
 *
 * Входные данные:
 * - categoryKey: string | null - ключ выбранной категории или null для сброса
 */

/**
 * Вспомогательная функция для поиска узла категории по ключу
 *
 * Назначение:
 * - Рекурсивно обходит дерево категорий для поиска узла по ключу
 * - Используется для получения данных категории по ее ключу
 *
 * Входные данные:
 * - nodes: ITreeNode[] - дерево категорий для поиска
 * - key: string - ключ искомого узла
 *
 * Возвращает:
 * - ITreeNode | null - найденный узел категории или null
 */
const findCategoryNode = (nodes: ITreeNode[], key: string): ITreeNode | null => {
  for (const node of nodes) {
    if (node.key === key) return node
    if (node.children.length > 0) {
      const found = findCategoryNode(node.children, key)
      if (found) return found
    }
  }
  return null
}

/** Подпись узла в ITreeNode-дереве по id/key */
function findTreeNodeLabel(nodes: ITreeNode[], id: string | null | undefined): string {
  if (!id || !nodes?.length) return ''
  for (const node of nodes) {
    if (node.key === id || node.data?.selfId === id)
      return node.label
    const found = findTreeNodeLabel(node.children || [], id)
    if (found) return found
  }
  return ''
}

/** Сводка полей вкладки «Форма» для вкладки координатора */
const formSummaryItems = computed(() => {
  const ticket = currentTicket.value
  const categoryKey = selectedCategory.value
  const categoryNode = categoryKey ? findCategoryNode(tickets.categoryTree, categoryKey) : null

  return [
    {
      label: 'Служба',
      value: ticket.service?.name || ticket.service?.short_name || findTreeNodeLabel(tickets.servicesTree, ticket.service?.id),
    },
    {
      label: 'Категория',
      value: categoryNode?.label || ticket.category?.name || '',
    },
    {
      label: 'Отделение',
      value: ticket.department?.name || findTreeNodeLabel(apiStore.departmentsTree, ticket.department?.id),
    },
    {
      label: 'Место',
      value: ticket.location_str || findTreeNodeLabel(apiStore.hospitalTree, ticket.location?.id),
    },
    { label: 'Контактный телефон', value: contactNumber.value || '' },
    { label: 'Проблема', value: description.value || '' },
    { label: 'Высокий приоритет', value: isHighPriority.value ? 'Да' : 'Нет' },
  ]
})

/**
 * Обработчик обновления местоположения для заявки
 *
 * Назначение:
 * - Обновляет данные о местоположении в объекте текущей заявки
 * - Синхронизирует выбор локации из NewTreeComponent с данными тикета
 * - Обеспечивает корректное сохранение выбранного местоположения
 *
 * Особенности:
 * - Поддерживает выбор локаций любого уровня вложенности
 * - Сохраняет идентификатор локации для последующего отображения
 * - Обрабатывает сценарии создания и редактирования заявки
 *
 * Входные данные:
 * - value: {id: string} | null - объект с ID выбранной локации или null для сброса
 *
 * Побочные эффекты:
 * - Модифицирует currentTicket.value.location
 * - Влияет на отображение места выполнения работ в заявке
 * - Участвует в валидации данных при отправке формы
 */
function onUpdateLocation(value: { id: string } | null) {
  currentTicket.value.location = value
}

// Ошибки валидации формы
const validationErrors = ref({
  service: false,
  category: false,
  description: false,
})

/**
 * Валидация формы перед отправкой
 *
 * Назначение:
 * - Проверяет заполнение обязательных полей формы
 * - Устанавливает флаги ошибок валидации
 * - Определяет возможность отправки формы
 *
 * Проверяемые поля:
 * - service: должна быть выбрана служба
 * - description: должно быть заполнено описание проблемы
 *
 * Возвращает:
 * - boolean: true если валидация пройдена, false если есть ошибки
 */
const validateForm = () => {
  const ticket = getCurrentTicket.value
  if (!ticket) return false

  validationErrors.value = {
    service: !ticket.service?.id,
    category: !ticket.category?.id,
    description: !ticket.description?.trim(),
  }

  return !Object.values(validationErrors.value).some(Boolean)
}

/**
 * Сохранение заявки. При создании: POST + sync файлов + закрытие формы.
 * При редактировании разбирает дельту: смена координатора, новые исполнители,
 * изменение тела. Форму закрывает только PATCH самого тикета — остальные
 * ветки оставляют её открытой для продолжения работы.
 */
const onTicketSubmit = async () => {
  if (!validateForm()) {
    feedbackStore.showToast({ type: 'error', message: 'Заполните все обязательные поля', time: 5000 })
    return
  }

  const id = entityId.value ? Number(entityId.value) : null
  let success = false

  try {
    if (!id) {
      const query = route.query
      const equipment = typeof query.equipment === 'string' && typeof query.equipmentCommand === 'string' ? { id: query.equipment, command: query.equipmentCommand } : undefined
      const created = await tickets.createTicket(currentTicket.value, equipment)
      if (!created?.id) {
        return
      }
      await syncTicketFiles(Number(created.id))
      success = true
      return
    }

    const editable = editableTicket.value
    const current = currentTicket.value
    if (!editable || !current) return

    const isTicketBodyChanged
      = current.description !== editable.description
      || current.contact_number !== editable.contact_number
      || current.is_high_priority !== editable.is_high_priority
      || current.service?.id !== editable.service?.id
      || current.category?.id !== editable.category?.id
      || current.location?.id !== editable.location?.id
      || current.department?.id !== editable.department?.id

    const initialCoordinatorId = editable.coordinator_entry?.responsible_employee?.id
    const currentCoordinatorId = current.coordinator_entry?.responsible_employee?.id
    const isCoordinatorChanged = initialCoordinatorId !== currentCoordinatorId

    if (isCoordinatorChanged && currentCoordinatorId) {
      await tickets.changeTicketCoordinator({
        event_id: id,
        coordinator_id: currentCoordinatorId,
      })
    }

    if (isTicketBodyChanged) {
      const updated = await tickets.updateTicket(id, current)
      if (!updated?.id) {
        return
      }
    }

    await syncTicketFiles(id)
    await tickets.fetchTicketById(id)
    success = true
  } catch (error) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(error, id ? 'Ошибка при обновлении заявки' : 'Ошибка при создании заявки'),
      time: 7000,
    })
  } finally {
    /* Закрываем только после создания; при редактировании форма остаётся открытой */
    if (success && !id) {
      visible.value = false
      if (props.isGlobal) {
        feedbackStore.globalTicketId = null
      } else {
        await feedbackStore.goToParentRoute()
      }
    }
  }
}

/**
 * Обработчик закрытия формы
 *
 * Назначение:
 * - Проверяет наличие несохраненных изменений перед закрытием
 * - Показывает диалог подтверждения если есть изменения
 * - Закрывает форму и возвращает на родительский маршрут
 *
 * Логика:
 * - В режиме создания: проверяет есть ли введенные данные
 * - В режиме редактирования: всегда разрешает закрытие
 */
const handleClose = () => {
  // Если создание тикета и есть изменения - показываем диалог
  if (hasFormChanges()) {
    showUnsavedChangesDialog.value = true
    return // не закрываем сразу
  }

  // Иначе закрываем
  visible.value = false
  feedbackStore.globalFormTab = null
  if (props.isGlobal) {
    feedbackStore.globalTicketId = null
  } else {
    feedbackStore.goToParentRoute()
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)

const initialCreateState = ref<string>('')

/**
 * Проверка наличия изменений в форме
 *
 * Назначение:
 * - Определяет были ли внесены изменения в данные заявки
 * - Используется для показа диалога подтверждения при закрытии
 *
 * Возвращает:
 * - boolean: true если есть изменения, false если форма пустая
 */
const hasFormChanges = (): boolean => {
  if (isAccessDenied.value) return false
  if (!currentTicket.value) return false
  if (ticketFiles.value?.length > 0) return true

  if (entityId.value) {
    return JSON.stringify(currentTicket.value) !== JSON.stringify(editableTicket.value)
  }

  if (!initialCreateState.value) return false

  return JSON.stringify(currentTicket.value) !== initialCreateState.value
}

/**
 * Обработчик подтверждения закрытия формы
 *
 * Назначение:
 * - Обрабатывает результат диалога подтверждения закрытия
 * - Закрывает форму при подтверждении
 * - Оставляет форму открытой при отмене
 *
 * Входные данные:
 * - value: boolean - результат подтверждения (true - закрыть, false - отменить)
 */
const handleCloseConfirm = async (value: boolean) => {
  if (value) {
    // Нажали "Закрыть"
    showUnsavedChangesDialog.value = false
    visible.value = false
    if (props.isGlobal) {
      feedbackStore.globalTicketId = null
    } else {
      await feedbackStore.goToParentRoute()
    }
  } else {
    // Нажали "Отменить"
    showUnsavedChangesDialog.value = false
  }
}

/**
 * Выбор координатора (кнопка «Назначить» в панели задач без задачи координатора) сразу уходит в API:
 * бэк создаёт задачу координатора, стор кладёт её в coordinator_entry текущей и эталонной заявки,
 * поэтому hasFormChanges смену грязью не считает. Оптимистичную подстановку не делаем:
 * задача без id отрисовалась бы в панели как настоящая.
 */
const onCoordinatorSelected = async (employee: IEmployeeTreeItem | null) => {
  const id = entityId.value ? Number(entityId.value) : null
  if (!employee || !id) return
  if (currentTicket.value.coordinator_entry?.responsible_employee?.id === employee.id) return

  const updatedEntry = await tickets.changeTicketCoordinator({
    event_id: id,
    coordinator_id: employee.id,
  })
  if (updatedEntry)
    await tickets.fetchTicketById(id)
}

/**
 * Назначение текущего пользователя координатором заявки
 */
const assignMyselfAsCoordinator = async () => {
  const currentUserId = userStore.user?.id
  const id = entityId.value ? Number(entityId.value) : null
  if (!currentUserId || !id) return

  try {
    await tickets.changeTicketCoordinator({
      event_id: id,
      coordinator_id: currentUserId,
    })
    await tickets.fetchTicketById(id)
    feedbackStore.showToast({
      type: 'success',
      message: 'Вы успешно назначены координатором заявки',
      time: 5000,
    })
  } catch (error) {
    console.error('Ошибка при назначении себя координатором:', error)
  }
}

/**
 * Открытие модалки выбора координатора
 *
 * Назначение:
 * - Показывает модальное окно выбора координатора
 * - Доступно пользователям с правом coordinator_edit и инспекторам (canPickAnyCoordinator)
 */
const openCoordinatorModal = () => {
  if (canPickAnyCoordinator.value && !isCompleted.value) {
    coordinatorModalRef.value?.open()
  }
}

/**
 * Взятие заявки в работу координатором
 *
 * Назначение:
 * - Выполняет API-запрос для взятия заявки в работу
 * - Доступно только для координаторов при наличии действия in-progress в actions
 */
const takeTicketOnWork = async () => {
  const eventId = Number(entityId.value)
  const entryId = tickets.currentTicket?.coordinator_entry?.id

  if (!entryId) {
    console.error('Не найден ID мероприятия координатора')
    return
  }
  await tickets.takeTicketOnWork({
    id: eventId,
    entry_id: entryId,
  })
}

/**
 * Возвращение заявки в работу
 *
 * Назначение:
 * - Выполняет API-запрос для возвращения заявки в работу
 * - Доступно только для координаторов или инспектора при наличии действия return в actions
 */
const returnTicketAtWork = async () => {
  const eventId = Number(entityId.value)
  const entryId = tickets.currentTicket?.coordinator_entry?.id

  if (!entryId) {
    console.error('Не найден ID мероприятия координатора')
    return
  }
  await tickets.returnTicketOnWork({
    id: eventId,
    entry_id: entryId,
  })
}


/**
 * Получение полного имени сотрудника
 */
const getEmployeeFullName = (employee: any) => {
  if (!employee) return '—'
  const { first_name, last_name, middle_name } = employee
  return [last_name, first_name, middle_name].filter(Boolean).join(' ') || '—'
}

/**
 * Открытие чата с сотрудником
 */
const handleChat = async (id: string) => {
  await openChat(id)
}

/**
 * Завершение заявки с проверками
 *
 * Назначение:
 * - Проверяет выполнение всех задач исполнителями перед завершением
 * - Показывает диалог подтверждения если есть незавершенные задачи
 * - Завершает заявку если все проверки пройдены
 *
 * Проверки:
 * - Все записи ответственности должны иметь статус 'completed'
 */
const completeTicketEvent = async () => {
  // Проверка: все ли подзадачи исполнителей завершены
  const subtasks = (currentTicket.value?.coordinator_entry as any)?.subtasks || []
  const hasUncompletedTasks = subtasks.some(
    (entry: any) => entry.status !== 'completed',
  )

  if (hasUncompletedTasks) {
    showUncompletedTasksDialog.value = true
    return
  }

  // Если все проверки пройдены - завершаем событие
  await completeEvent()
}

/**
 * Функция завершения заявки (после проверок)
 *
 * Назначение:
 * - Выполняет API-запрос для завершения заявки
 * - Закрывает форму и возвращает на родительский маршрут
 * - Обрабатывает ошибки завершения
 */

/**
 * Открытие компонента модалки завершения НС
 */
const completeEvent = async () => {
  apiStore.isShowCoordinatorReport = true
}

/**
 * Завершает отработку заявки (ticket).
 *
 * Выполняет:
 * - Скрывает текущий диалог или форму, устанавливая `visible` в false
 * - Перенаправляет пользователя на родительский маршрут через `goToParentRoute()`
 * - Обновляет список заявок, вызывая `fetchTickets()`
 */
const eventComplete = async () => {
  if (props.isGlobal) {
    feedbackStore.globalTicketId = null
    visible.value = false
  } else {
    visible.value = false
    await feedbackStore.goToParentRoute()
    await tickets.fetchTickets()
  }
}

/**
 * Удаление существующего файла заявки
 *
 * Назначение:
 * - Добавляет ID файла в список помеченных на удаление
 * - Немедленно обновляет список файлов в интерфейсе
 *
 * Входные данные:
 * - id: number - идентификатор файла для удаления
 */
function onExistingTicketFileRemove(id: number): void {
  if (!ticketRemovedFileIds.value.includes(id)) ticketRemovedFileIds.value.push(id)
  ;(tickets.currentTicket as any).files = existingTicketFiles.value.filter(f => f.id !== id)
}

/**
 * Обновление локального списка новых файлов заявки
 *
 * Назначение:
 * - Синхронизирует локальное состояние с выбранными файлами
 * - Обеспечивает реактивное обновление интерфейса
 *
 * Входные данные:
 * - v: File[] - массив выбранных файлов
 */
function onTicketFilesUpdate(v: File[]): void {
  ticketFiles.value = Array.isArray(v) ? v : []
}

/**
 * Синхронизация файлов заявки с сервером
 *
 * Назначение:
 * - Загружает новые файлы на сервер
 * - Удаляет помеченные файлы с сервера
 * - Обновляет данные заявки после синхронизации
 *
 * Алгоритм:
 * 1. Проверяет наличие новых файлов и файлов на удаление
 * 2. Параллельно выполняет загрузку и удаление
 * 3. Очищает локальные состояния после завершения
 * 4. Обновляет данные заявки для отображения актуальных файлов
 *
 * Входные данные:
 * - ticketId: number - идентификатор заявки
 */
async function syncTicketFiles(ticketId: number): Promise<void> {
  const hasNew = Array.isArray(ticketFiles?.value) && ticketFiles.value.length > 0
  const hasDel = Array.isArray(ticketRemovedFileIds?.value) && ticketRemovedFileIds.value.length > 0
  if (!hasNew && !hasDel) return

  const tasks: Promise<any>[] = []

  if (hasNew) {
    tasks.push(filesStore.uploadTicketFiles(ticketId, ticketFiles.value, 'update'))
  }

  if (hasDel) {
    for (const fid of ticketRemovedFileIds.value) {
      tasks.push(filesStore.deleteTicketFile(ticketId, fid))
    }
  }

  await Promise.allSettled(tasks)

  ticketFiles.value = []
  ticketRemovedFileIds.value = []

  // Обновляем данные тикета
  try {
    await tickets.fetchTicketById(ticketId)
  } catch (error) {
    console.error('Ошибка при обновлении тикета:', error)
  }
}

/**
 * Обработчик подтверждения в диалогах
 *
 * Назначение:
 * - Обрабатывает результаты диалогов подтверждения действий
 * - Выполняет соответствующие действия при подтверждении
 * - Закрывает диалоги при отмене
 *
 * Входные данные:
 * - value: boolean - результат подтверждения (true - подтвердить, false - отменить)
 */
const handleConfirm = (value: boolean) => {
  if (value) {
    // Нажали "Завершить"
    completeEvent()
    showUncompletedTasksDialog.value = false
  } else {
    // Нажали "Отменить"
    showUncompletedTasksDialog.value = false
  }
}

/**
 * Получение текста статуса по коду
 *
 * @param status - код статуса
 * @returns текстовое представление статуса
 */
const getStatusText = (status: string | undefined) => {
  if (!status) return 'Ожидает'
  const statusMap = apiStore.responsibilityEntriesStatuses
  return statusMap[status as keyof typeof statusMap] || status
}

const setDefaultDepartment = () => {
  if (!currentTicket.value.department) {
    const userDepartment = userStore.user?.department?.id
    if (userDepartment) {
      currentTicket.value.department = { id: userDepartment }
    }
  }
  initialCreateState.value = JSON.stringify(currentTicket.value)
}

// Рекурсивный сбор ID службы и всех её потомков
function getServiceIdsWithChildren(services: ITicketService[], targetId: string): string[] {
  const result: string[] = []
  function collectIds(service: ITicketService) {
    result.push(service.id)
    if (service.children) {
      for (const child of service.children) {
        collectIds(child)
      }
    }
  }
  function findAndCollect(list: ITicketService[]): boolean {
    for (const service of list) {
      if (service.id === targetId) {
        collectIds(service)
        return true
      }
      if (service.children && service.children.length > 0) {
        const found = findAndCollect(service.children)
        if (found) return true
      }
    }
    return false
  }
  findAndCollect(services)
  return result.length > 0 ? result : [targetId]
}

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * 1. Загружает отделения, сотрудников и категории если они еще не загружены
 * 2. В режиме редактирования загружает данные заявки по ID из маршрута
 * 3. Восстанавливает выбор категории через findKeyById если необходимо
 */
onMounted(async () => {
  const id = entityId.value

  feedbackStore.isGlobalLoading = true
  try {
    await Promise.all([
      !apiStore.departments?.length && apiStore.fetchAllDepartments(),
      !tickets.categoryTree.length && tickets.fetchNewCategories(),
      !apiStore.employees?.length && apiStore.fetchAllEmployees(),
      !apiStore.hospitalSkeleton?.length && apiStore.fetchHospitalSkeleton(),
    ])
  } finally {
    feedbackStore.isGlobalLoading = false
  }

  if (isDemo.value) {
    applyDemoTicket()
    return
  }

  if (id) {
    try {
      isAccessDenied.value = false
      await tickets.fetchTicketById(Number(id))
      await loadHistoryLogs()
    } catch (e) {
      if (isHttpNotFound(e))
        isAccessDenied.value = true
    }
    return
  }

  isAccessDenied.value = false
  setDefaultDepartment()
})

watch(entityId, async newId => {
  if (formTabValue.value === 3)
    formTabValue.value = 0
  if (isDemo.value) {
    applyDemoTicket()
    return
  }
  if (newId) {
    try {
      isAccessDenied.value = false
      await tickets.fetchTicketById(Number(newId))
      await loadHistoryLogs()
    } catch (e) {
      if (isHttpNotFound(e))
        isAccessDenied.value = true
    }
  } else {
    isAccessDenied.value = false
    setDefaultDepartment()
  }
})

async function loadHistoryLogs() {
  const id = entityId.value
  if (!id) return
  try {
    await tickets.fetchTicketLogs(Number(id))
  } catch (error) {
    console.error('Ошибка загрузки истории заявки:', error)
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(error, 'Не удалось загрузить историю изменений'),
      time: 5000,
    })
  }
}

const hasHistoryLogs = computed(() =>
  !!entityId.value && tickets.ticketLogs.length > 0,
)

/*
 * Онбординг: форма — контекст шагов по полям и «ушкам», панель задач — вложенный контекст.
 * showDemo/showReal подменяют реальную заявку учебной, когда обучение запущено из открытой формы.
 */
useOnboardingContext({
  names: [TICKETS_CONTEXTS.form, TICKETS_CONTEXTS.formTasks],
  scenarioId: 'tickets.form',
  root: () => document.body,
  actions: {
    showDemo: () => {
      demoOverrideId.value = onboardingStore.demo.ticket?.[TICKETS_FORM_DEMO_INDEX]?.detail.id ?? null
      formTabValue.value = 0
      applyDemoTicket()
    },
    showReal: async () => {
      demoOverrideId.value = null
      isTasksPaneVisible.value = false
      isDemoChatVisible.value = false
      if (entityId.value && !isDemo.value) {
        await tickets.fetchTicketById(Number(entityId.value))
        await loadHistoryLogs()
      }
    },
    openTasksPane: () => {
      if (!isTasksPaneActive.value)
        toggleTasksPane()
    },
    closeTasksPane: () => { isTasksPaneVisible.value = false },
    openDemoChat: () => {
      if (!isDemoChatVisible.value)
        toggleDemoChat()
    },
    closeDemoChat: () => { isDemoChatVisible.value = false },
  },
})

</script>

<template>
  <!-- Blur-подложка для embedded-режима (по образцу PinUnlock.vue:376-381). -->
  <Teleport to="body">
    <div
      v-if="formEmbeddedLayout.isEmbedded.value && visible"
      class="entity-form-backdrop"
    />
  </Teleport>

  <VDialog
    v-model="visible"
    :z-index="2400"
    :max-width="formEmbeddedLayout.maxWidth.value"
    :content-class="overlayContentClass"
    :scrim="!formEmbeddedLayout.isEmbedded.value"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <Teleport to="body">
      <div
        v-if="formEmbeddedLayout.isEmbedded.value && visible"
        class="entity-form-actions"
      >
        <!-- Задачи и чат — вкладки правой панели: активная подсвечена, повторный клик скрывает панель -->
        <EntityPaneEar
          v-if="entityId && hasTasksPane"
          kind="tasks"
          :active="isTasksPaneActive"
          @click="toggleTasksPane"
        />
        <EntityPaneEar
          v-if="entityChat.isAvailable.value"
          kind="chat"
          :active="isChatPaneActive"
          @click="toggleChatPane"
        />
        <EntityPaneEar
          v-else-if="isDemo"
          kind="chat"
          :active="isDemoChatVisible"
          @click="toggleDemoChat"
        />
        <DownloadEntityDocument
          v-if="entityId"
          variant="ear"
          :path="`/api/ticket/ticket/${entityId}/document/`"
          :fallback-name="`ticket_${currentTicket.number || entityId}`"
        />
        <ReportTemplateMenu v-if="entityId" object-type="ticket" :object-id="entityId" variant="ear" />
        <CopyEntityLink
          v-if="entityId"
          variant="ear"
          :to="{ name: ERouteNames.TICKET_DETAIL, params: { id: entityId } }"
        />
        <OnboardingLaunchButton v-if="entityId" variant="ear" />
        <VBtn
          icon="ri-close-line"
          color="primary"
          class="entity-form-action-btn"
          aria-label="Закрыть"
          @click="handleClose"
        />
      </div>
    </Teleport>

    <VCard :class="{ 'entity-form-card h-100': formEmbeddedLayout.isEmbedded.value }">
      <!--
        VCardTitle перенесён внутрь pane — в embedded-режиме заголовок
        занимает только ширину формы, чат тянется от верхнего края card.
      -->
      <div
        :class="(formEmbeddedLayout.isEmbedded.value || pinCreateChrome)
          ? 'entity-form-body d-flex h-100 overflow-hidden'
          : 'entity-form-mobile-pane-body'"
      >
        <!-- Панель кнопок разделов на мобильных устройствах НАД названием; закреплена при прокрутке (только при просмотре заявки на мобилке) -->
        <div
          v-if="isEdit && !formEmbeddedLayout.isEmbedded.value && xs"
          class="entity-form-mobile-actions d-flex align-center justify-space-evenly w-100"
        >
          <VTooltip
            v-if="hasTasksPane"
            location="bottom"
            :text="isTasksPaneActive ? 'Скрыть задачи' : 'Показать задачи'"
          >
            <template #activator="{ props: taskToggleProps }">
              <VBtn
                v-bind="taskToggleProps"
                icon="ri-list-check"
                variant="text"
                :color="isTasksPaneActive ? 'secondary' : 'primary'"
                aria-label="Задачи по заявке"
                @click="toggleTasksPane"
              />
            </template>
          </VTooltip>
          <VTooltip
            v-if="entityChat.isAvailable.value"
            location="bottom"
            text="Чат задачи"
          >
            <template #activator="{ props: eventChatProps }">
              <VBtn
                v-bind="eventChatProps"
                icon="ri-chat-1-line"
                variant="text"
                color="primary"
                @click="entityChat.openInSlidingChat"
              />
            </template>
          </VTooltip>
          <DownloadEntityDocument
            v-if="entityId"
            variant="inline"
            :path="`/api/ticket/ticket/${entityId}/document/`"
            :fallback-name="`ticket_${currentTicket.number || entityId}`"
          />
          <ReportTemplateMenu v-if="entityId" object-type="ticket" :object-id="entityId" variant="inline" />
          <CopyEntityLink
            v-if="entityId"
            variant="inline"
            :to="{ name: ERouteNames.TICKET_DETAIL, params: { id: entityId } }"
          />
          <OnboardingLaunchButton v-if="entityId" variant="inline" />
          <CustomCloseButton :close-action="handleClose" />
        </div>
        <div
          :class="(formEmbeddedLayout.isEmbedded.value || pinCreateChrome)
            ? 'entity-form-pane flex-grow-1 position-relative'
            : (isMobileTasksView ? 'position-relative d-flex flex-column' : 'position-relative d-flex flex-column flex-grow-1')"
        >
          <EntityAccessDeniedOverlay v-if="isAccessDenied" variant="form" />
          <VCardTitle
            class="d-flex pa-6"
            :class="{
              'flex-column pb-2': isEdit && !formEmbeddedLayout.isEmbedded.value && xs,
              'px-4 py-3': xs,
            }"
            :style="isAccessDenied ? { position: 'relative', zIndex: 6 } : undefined"
          >

            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title">
                <span class="text-h4" :class="{ 'font-weight-semibold': isEdit }"> {{ cardTitle }}</span>
              </div>

              <!-- В обычном режиме на десктопе ИЛИ в режиме создания на всех экранах кнопки справа в строке -->
              <div
                v-if="!formEmbeddedLayout.isEmbedded.value && (!xs || !isEdit)"
                class="d-flex align-center gap-2"
              >
                <template v-if="isEdit">
                  <VTooltip
                    v-if="hasTasksPane"
                    location="bottom"
                    :text="isTasksPaneActive ? 'Скрыть задачи' : 'Показать задачи'"
                  >
                    <template #activator="{ props: taskToggleProps }">
                      <VBtn
                        v-bind="taskToggleProps"
                        icon="ri-list-check"
                        variant="text"
                        :color="isTasksPaneActive ? 'secondary' : 'primary'"
                        aria-label="Задачи по заявке"
                        @click="toggleTasksPane"
                      />
                    </template>
                  </VTooltip>
                  <VTooltip
                    v-if="entityChat.isAvailable.value"
                    location="bottom"
                    text="Чат задачи"
                  >
                    <template #activator="{ props: eventChatProps }">
                      <VBtn
                        v-bind="eventChatProps"
                        icon="ri-chat-1-line"
                        variant="text"
                        color="primary"
                        @click="entityChat.openInSlidingChat"
                      />
                    </template>
                  </VTooltip>
                  <DownloadEntityDocument
                    v-if="entityId"
                    variant="inline"
                    :path="`/api/ticket/ticket/${entityId}/document/`"
                    :fallback-name="`ticket_${currentTicket.number || entityId}`"
                  />
                  <ReportTemplateMenu v-if="entityId" object-type="ticket" :object-id="entityId" variant="inline" />
                  <CopyEntityLink
                    v-if="entityId"
                    variant="inline"
                    :to="{ name: ERouteNames.TICKET_DETAIL, params: { id: entityId } }"
                  />
                  <OnboardingLaunchButton v-if="entityId" variant="inline" />
                </template>
                <CustomCloseButton :close-action="handleClose" />
              </div>
            </div>
          </VCardTitle>

          <VForm :class="(!formEmbeddedLayout.isEmbedded.value && !pinCreateChrome) ? (isMobileTasksView ? 'd-none' : 'd-flex flex-column flex-grow-1') : undefined" @submit="onTicketSubmit">
                <VAlert v-if="route.query.equipment" type="info" variant="tonal" class="mb-4">Заявка будет связана с аппаратом, из карточки которого вы перешли.</VAlert>
            <VTabs v-if="isEdit" v-model="formTabValue">
              <div class="d-flex gap-2">
                <VTab :value="0">
                  Форма
                </VTab>
                <VTab v-if="permissionsStore.canViewChangeHistory && hasHistoryLogs" :value="3" :data-onboarding="TICKETS_ANCHORS.form.historyTab">
                  История
                </VTab>
              </div>

              <div class="d-flex gap-2 align-center">
                <!-- Чип статуса заявки -->
                <VChip
                  v-if="entityId"
                  :class="currentTicket.status"
                  class="status-badge"
                  size="small"
                >
                  {{ getStatusText(currentTicket.status) }}
                </VChip>

                <!-- Чип высокого приоритета -->
                <VChip
                  v-if="entityId && currentTicket.is_high_priority"
                  class="high-priority-chip"
                  variant="outlined"
                  size="small"
                >
                  Приоритетная
                </VChip>
              </div>
            </VTabs>

            <VWindow v-model="formTabValue" class="mt-4 flex-grow-1">
              <VWindowItem :value="0">
                <div class="entity-form-scroll-body">
                    <VCol class="d-flex flex-column gap-4 px-0">
                      <div class="d-flex gap-2" :class="{ 'flex-column': !mdAndUp }" :data-onboarding="TICKETS_ANCHORS.form.serviceCategory">
                        <!-- Выбор службы из дерева -->
                        <NewTreeComponent
                          v-model:visible="showServiceSelect"
                          :disabled="!canEditField('service')"
                          placeholder="Выберите службу"
                          label="Служба"
                          is-label-required
                          search-placeholder="Поиск по службам"
                          :selected="{ value: currentTicket.service ?? null }"
                          :tree-data="tickets.servicesTree"
                          :search-tree="searchServiceTree"
                          :error="validationErrors.service"
                          :class="!mdAndUp ? 'w-100' : 'w-50'"
                          @update:selected="onServiceSelect"
                        />

                        <!-- Выбор категории из дерева -->
                        <NewTreeComponent
                          v-model:visible="showCategoryTree"
                          :disabled="!canEditField('category')"
                          placeholder="Выберите категорию"
                          label="Категория"
                          is-label-required
                          search-placeholder="Поиск по категориям"
                          :selected="{ value: selectedCategory ? { id: selectedCategory } : (currentTicket.category ? { id: String(currentTicket.category.id), name: currentTicket.category.name } : null) }"
                          :tree-data="categoryTree"
                          :search-tree="searchCategoryTree"
                          :error="validationErrors.category"
                          :class="!mdAndUp ? 'w-100 mt-2' : 'w-50'"
                          @update:selected="(val) => selectedCategory = val ? val.id : null"
                        />
                      </div>

                      <div :data-onboarding="TICKETS_ANCHORS.form.department">
                        <NewTreeComponent
                          v-model:visible="showDepartmentSelect"
                          :disabled="!canEditField('department')"
                          placeholder="Выберите отделение"
                          label="Отделение"
                          search-placeholder="Поиск по отделениям"
                          :selected="{ value: currentTicket.department ?? null }"
                          :tree-data="apiStore.departmentsTree"
                          :search-tree="searchDepartments"
                          @update:selected="onDepartmentSelect"
                        />
                      </div>

                      <div :data-onboarding="TICKETS_ANCHORS.form.location">
                        <NewTreeComponent
                          v-model:visible="showRoomModal"
                          :disabled="!canEditField('location')"
                          placeholder="Выберите место"
                          label="Место"
                          :selected="{ value: currentTicket?.location ?? null }"
                          :tree-data="apiStore.hospitalTree"
                          :search-tree="searchHospitalTree"
                          search-placeholder="Поиск по помещениям"
                          @update:selected="onUpdateLocation"
                        />
                      </div>

                      <div>
                        <VTextField
                          v-model="contactNumber"
                          :readonly="!canEditField('contact_number')"
                          density="comfortable"
                          variant="outlined"
                          label="Контактный телефон"
                          placeholder="Для уточняющих вопросов"
                          persistent-placeholder
                        />
                      </div>

                      <div class="d-flex gap-2 flex-column">
                        <div class="d-flex">
                          <VTextarea
                            v-model="description"
                            :readonly="!canEditField('description')"
                            density="comfortable"
                            rows="3"
                            variant="outlined"
                            placeholder="Опишите проблему"
                            persistent-placeholder
                            :error="validationErrors.description"
                          >
                            <template #label>
                              <span>Проблема<sup class="required-label">*</sup></span>
                            </template>
                          </VTextarea>
                        </div>

                        <div class="d-flex align-center gap-4 flex-wrap" :data-onboarding="canEditField('files') ? TICKETS_ANCHORS.form.files : undefined">
                          <VBtn
                            :class="xs ? 'flex-grow-1' : 'align-self-start'"
                            variant="outlined"
                            class="flex-shrink-1"
                            color="secondary"
                            prepend-icon="ri-attachment-line"
                            :disabled="!canEditField('files')"
                            @click="fileAttachRef?.openDialog()"
                          >
                            Прикрепить файлы
                          </VBtn>
                          <span
                            v-if="!smAndDown"
                            class="text-body-2 paste-hint"
                          >или вставьте файлы из буфера (Ctrl+V)</span>
                        </div>
                      </div>

                      <VCheckbox
                        v-model="isHighPriority"
                        :disabled="!canEditField('is_high_priority')"
                        label="Высокий приоритет"
                      />

                      <FileAttach
                        ref="fileAttachRef"
                        :files="ticketFiles"
                        :disabled="!canEditField('files')"
                        accept="*"
                        @update:files="onTicketFilesUpdate"
                      />

                      <FileList
                        v-if="existingTicketFiles?.length"
                        class="ticket-form__files-existing"
                        :items="existingTicketFiles"
                        :show-delete="canEditField('files')"
                        @remove="onExistingTicketFileRemove"
                      />

                      <CustomDivider v-if="entityId && currentTicket?.coordinator_entry?.last_report && isCompleted && (isCreator || isCoordinator)" />

                      <VTextarea
                        v-if="currentTicket?.coordinator_entry && isCompleted && (isCreator || isCoordinator)"
                        v-model="currentTicket.coordinator_entry.last_report"
                        readonly
                        density="comfortable"
                        rows="3"
                        variant="outlined"
                      >
                        <template #label>
                          <span>Отчет по заявке</span>
                        </template>
                      </VTextarea>
                    </VCol>
                  </div>
                </VWindowItem>



                <VWindowItem :value="3">
                  <div class="entity-form-scroll-body">
                    <HistoryLogsTable
                      :logs="tickets.ticketLogs"
                      @click-user="openEmployeeDetail"
                    />
                  </div>
                </VWindowItem>
              </VWindow>

              <footer
                v-if="formTabValue === 0 || formTabValue === 1"
                class="d-flex flex-column gap-2 body-2"
              >
                <CustomDivider v-if="isEdit" />

                <div
                  class="d-flex gap-4"
                  :class="xs ? 'flex-column' : 'align-start justify-space-between'"
                >
                  <div
                    v-if="isEdit"
                    class="d-flex flex-column gap-2"
                    :class="{ 'order-2': xs }"
                  >
                    <div class="d-flex gap-2 align-center">
                      <VIcon icon="ri-user-3-line" size="16" />
                      <span
                        class="cursor-pointer"
                        @click="currentTicket?.created_by?.id && openEmployeeDetail(currentTicket.created_by.id)"
                      >
                        Создатель: {{ getFullName(currentTicket?.created_by ?? {}) }}
                      </span>
                      <VTooltip>
                        <template #activator="{ props: activatorProps }">
                          <VIcon
                            icon="ri-information-line"
                            size="10"
                            v-bind="activatorProps"
                          />
                        </template>
                        <div>
                          {{ creatorDepartmentTooltip }}
                        </div>
                      </VTooltip>
                    </div>

                    <div class="d-flex gap-2 align-center">
                      <VIcon icon="ri-time-line" size="16" />
                      <span>Дата и время регистрации: {{ formatDateTime(currentTicket?.created_at) }}</span>
                    </div>
                  </div>
                  <div v-else-if="!xs" class="flex-grow-1" />

                  <div
                    class="d-flex gap-2"
                    :class="xs ? 'order-1 flex-column' : 'flex-shrink-0 align-center'"
                  >
                    <VBtn
                      v-if="canAssignMyself"
                      prepend-icon="ri-user-shared-line"
                      variant="outlined"
                      :block="xs"
                      :data-onboarding="TICKETS_ANCHORS.form.assignSelf"
                      @click="assignMyselfAsCoordinator"
                    >
                      Назначить себя
                    </VBtn>

                    <VBtn
                      v-if="formTabValue === 0 && (!isEdit || currentTicket.editable_fields?.length || !isCompleted)"
                      :block="xs"
                      :data-onboarding="currentTicket.editable_fields?.length ? TICKETS_ANCHORS.form.save : undefined"
                      @click="onTicketSubmit"
                    >
                      {{ actionButtonText }}
                    </VBtn>
                  </div>
                </div>
              </footer>
            </VForm>
        </div>
        <!-- Разделитель между формой и правой панелью — перетягивается мышкой. -->
        <div
          v-if="formEmbeddedLayout.isEmbedded.value && ((isTasksPaneVisible && hasTasksPane) || (entityChat.isChatVisible.value && entityChat.isAvailable.value) || isDemoChatVisible)"
          class="entity-form-resizer"
          :class="{ 'is-resizing': chatPaneResizer.isResizing.value }"
          @mousedown="chatPaneResizer.startResize"
        />
        <!--
          Панель задач (TaskCard; без задачи координатора — TaskUnassignedCard с «Назначить»/«Назначить себя»)
          или встроенный чат группы заявки справа (чат — только embedded-режим).
          Без embedded панель задач открывается под шапкой формы вместо её тела.
        -->
        <div
          v-if="isTasksPaneActive"
          :class="formEmbeddedLayout.isEmbedded.value
            ? 'entity-form-chat-pane overflow-hidden pa-0 d-flex flex-column'
            : 'd-flex flex-column flex-grow-1'"
          :style="formEmbeddedLayout.isEmbedded.value ? { flexBasis: `${chatPaneResizer.width.value}px` } : undefined"
        >
          <TaskCard
            class="flex-grow-1"
            v-if="coordinatorTask"
            :task="coordinatorTask"
            :is-demo="isDemo"
            @updated="handleCoordinatorTaskUpdated"
          />
          <TaskUnassignedCard
            v-else
            :data-onboarding="TICKETS_ANCHORS.form.assignCoordinator"
            title="Задача координатора"
            :performer-label="'Исполнитель -\nкоординатор'"
            hint="Координатор ещё не назначен"
            :can-assign="(canPickAnyCoordinator || canAssignMyself) && !isCompleted"
            :assign-label="canPickAnyCoordinator ? 'Назначить' : 'Назначить себя'"
            @assign="canPickAnyCoordinator ? openCoordinatorModal() : assignMyselfAsCoordinator()"
          />
        </div>
        <BridgedEmbeddedChat
          v-else-if="formEmbeddedLayout.isEmbedded.value && entityChat.isChatVisible.value && entityChat.isAvailable.value"
          :chat-id="entityChat.chatId.value"
          :connect-error="entityChat.connectError.value"
          class="entity-form-chat-pane"
          :style="{ flexBasis: `${chatPaneResizer.width.value}px` }"
          @hide-chat="entityChat.hideChat"
        />
        <OnboardingDemoChat
          v-else-if="formEmbeddedLayout.isEmbedded.value && isDemoChatVisible && demoItem"
          :chat="demoItem.chat"
          class="entity-form-chat-pane"
          :style="{ flexBasis: `${chatPaneResizer.width.value}px` }"
        />
      </div>
    </VCard>
  </VDialog>

  <!-- Выбор координатора (кнопка «Назначить» в панели задач без задачи координатора) -->
  <EmployeeSelectionModal
    ref="coordinatorModalRef"
    :employees="apiStore.employees"
    :multiple="false"
    title="Выберите координатора"
    apply-label="Назначить"
    @select="onCoordinatorSelected"
  />

  <!-- Диалог для незавершенных задач -->
  <ConfirmDialog
    v-model:is-dialog-visible="showUncompletedTasksDialog"
    confirmation-question="Не все исполнители отчитались"
    confirmation-text="Вы уверены, что хотите завершить заявку?"
    confirm-btn="Завершить"
    reject-btn="Отменить"
    @confirm="handleConfirm"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />

  <EmployeeProfileSidebar
    v-model="isEmployeeModalOpen"
    :employee-id="employeeId"
  />

  <CoordinatorReport target="ticket" @submit-form="eventComplete" />
</template>

<style scoped lang="scss">
.high-priority-chip {
  color: rgb(var(--v-theme-error)) !important;
  border-color: rgb(var(--v-theme-error)) !important;
}

:deep(.v-slide-group__content) {
  justify-content: space-between;
}

.paste-hint {
  color: rgba(var(--v-theme-text-primary), 0.55);
}
</style>
